import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { parseApiError } from '../lib/parseApiError';
import { 
  Building2, 
  Plus, 
  Users, 
  Ticket, 
  Printer, 
  Settings,
  Trash2,
  Edit,
  Power,
  BarChart3,
  Calendar,
  Mail,
  Phone,
  MapPin,
  CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminPage() {
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email_admin: '',
    password_admin: '',
    telefono: '',
    indirizzo: '',
    citta: '',
    partita_iva: '',
    piano: 'base',
    max_utenti: 5,
    scadenza_abbonamento: '',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        axios.get(`${API}/tenants`),
        axios.get(`${API}/tenants/stats`)
      ]);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accesso riservato al Super Admin');
      } else {
        toast.error('Errore nel caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTenant) {
        await axios.put(`${API}/tenants/${editingTenant.id}`, formData);
        toast.success('Tenant aggiornato');
      } else {
        await axios.post(`${API}/tenants`, formData);
        toast.success('Tenant creato con successo');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const handleToggleStatus = async (tenant) => {
    try {
      await axios.post(`${API}/tenants/${tenant.id}/toggle-status`);
      toast.success(`Tenant ${tenant.attivo ? 'disattivato' : 'attivato'}`);
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Eliminare ${tenant.nome} e TUTTI i suoi dati? Questa azione è irreversibile!`)) {
      return;
    }
    try {
      await axios.delete(`${API}/tenants/${tenant.id}`);
      toast.success('Tenant eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const openEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      nome: tenant.nome,
      email_admin: tenant.email_admin,
      password_admin: '',
      telefono: tenant.telefono || '',
      indirizzo: tenant.indirizzo || '',
      citta: tenant.citta || '',
      partita_iva: tenant.partita_iva || '',
      piano: tenant.piano || 'base',
      max_utenti: tenant.max_utenti || 5,
      scadenza_abbonamento: tenant.scadenza_abbonamento || '',
      note: tenant.note || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingTenant(null);
    setFormData({
      nome: '',
      email_admin: '',
      password_admin: '',
      telefono: '',
      indirizzo: '',
      citta: '',
      partita_iva: '',
      piano: 'base',
      max_utenti: 5,
      scadenza_abbonamento: '',
      note: ''
    });
  };

  const getStatForTenant = (tenantId) => {
    return stats.find(s => s.tenant_id === tenantId) || {};
  };

  const getPianoColor = (piano) => {
    switch (piano) {
      case 'enterprise': return 'bg-purple-500';
      case 'pro': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Summary stats
  const totalTenants = tenants.length;
  const activeTenants = tenants.filter(t => t.attivo).length;
  const totalUtenti = stats.reduce((sum, s) => sum + s.num_utenti, 0);
  const totalClienti = stats.reduce((sum, s) => sum + s.num_clienti, 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-black tracking-tight">SUPER ADMIN</h1>
          <p className="text-sm text-muted-foreground">Gestione Aziende Cliente (Multi-Tenant)</p>
        </div>
        <Button className="rounded-none" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nuova Azienda
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-none">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-sm">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalTenants}</p>
              <p className="text-xs text-muted-foreground">Aziende Totali</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-success/10 rounded-sm">
              <Power className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeTenants}</p>
              <p className="text-xs text-muted-foreground">Aziende Attive</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-sm">
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalUtenti}</p>
              <p className="text-xs text-muted-foreground">Utenti Totali</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-none">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-orange-500/10 rounded-sm">
              <BarChart3 className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalClienti}</p>
              <p className="text-xs text-muted-foreground">Clienti Totali</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {tenants.map((tenant) => {
          const stat = getStatForTenant(tenant.id);
          return (
            <Card key={tenant.id} className={`rounded-none ${!tenant.attivo ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-sm ${tenant.attivo ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Building2 className={`h-5 w-5 ${tenant.attivo ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{tenant.nome}</CardTitle>
                      <p className="text-xs text-muted-foreground font-mono">{tenant.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-none text-xs text-white ${getPianoColor(tenant.piano)}`}>
                      {tenant.piano?.toUpperCase()}
                    </Badge>
                    {!tenant.attivo && (
                      <Badge variant="destructive" className="rounded-none text-xs">
                        DISATTIVATO
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-3 w-3" />
                    {tenant.email_admin}
                  </div>
                  {tenant.telefono && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {tenant.telefono}
                    </div>
                  )}
                  {tenant.citta && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      {tenant.citta}
                    </div>
                  )}
                  {tenant.scadenza_abbonamento && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      Scade: {tenant.scadenza_abbonamento}
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border">
                  <div className="text-center">
                    <p className="text-lg font-bold">{stat.num_utenti || 0}</p>
                    <p className="text-xs text-muted-foreground">Utenti</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold">{stat.num_clienti || 0}</p>
                    <p className="text-xs text-muted-foreground">Clienti</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold">{stat.num_impianti || 0}</p>
                    <p className="text-xs text-muted-foreground">Impianti</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold">{stat.num_ticket_aperti || 0}</p>
                    <p className="text-xs text-muted-foreground">Ticket</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-border">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-none flex-1"
                    onClick={() => openEdit(tenant)}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Modifica
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-none"
                    onClick={() => handleToggleStatus(tenant)}
                  >
                    <Power className={`h-3 w-3 ${tenant.attivo ? 'text-destructive' : 'text-success'}`} />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-none text-destructive"
                    onClick={() => handleDelete(tenant)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {tenants.length === 0 && (
        <Card className="rounded-none">
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-bold mb-2">Nessuna azienda configurata</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Crea la prima azienda cliente per iniziare
            </p>
            <Button className="rounded-none" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuova Azienda
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono uppercase">
              {editingTenant ? 'Modifica Azienda' : 'Nuova Azienda Cliente'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs font-mono uppercase">Nome Azienda *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  className="rounded-none"
                  required
                  placeholder="Es: Rossi Srl"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Email Admin *</Label>
                <Input
                  type="email"
                  value={formData.email_admin}
                  onChange={(e) => setFormData({...formData, email_admin: e.target.value})}
                  className="rounded-none"
                  required
                  disabled={!!editingTenant}
                  placeholder="admin@azienda.it"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">
                  Password Admin {editingTenant ? '(lascia vuoto per non modificare)' : '*'}
                </Label>
                <Input
                  type="password"
                  value={formData.password_admin}
                  onChange={(e) => setFormData({...formData, password_admin: e.target.value})}
                  className="rounded-none"
                  required={!editingTenant}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Telefono</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Città</Label>
                <Input
                  value={formData.citta}
                  onChange={(e) => setFormData({...formData, citta: e.target.value})}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">P. IVA</Label>
                <Input
                  value={formData.partita_iva}
                  onChange={(e) => setFormData({...formData, partita_iva: e.target.value})}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Scadenza Abbonamento</Label>
                <Input
                  type="date"
                  value={formData.scadenza_abbonamento}
                  onChange={(e) => setFormData({...formData, scadenza_abbonamento: e.target.value})}
                  className="rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Piano</Label>
                <Select 
                  value={formData.piano} 
                  onValueChange={(value) => setFormData({...formData, piano: value})}
                >
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="base">Base (5 utenti)</SelectItem>
                    <SelectItem value="pro">Pro (15 utenti)</SelectItem>
                    <SelectItem value="enterprise">Enterprise (illimitati)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Max Utenti</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.max_utenti}
                  onChange={(e) => setFormData({...formData, max_utenti: parseInt(e.target.value) || 5})}
                  className="rounded-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" className="rounded-none">
                {editingTenant ? 'Salva Modifiche' : 'Crea Azienda'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
